import { useState } from 'react'
import { useCreateTarget } from '@/api/targets'
import {
  buildAsinExpression,
  buildBrandExpression,
  buildCategoryExpression,
  ASIN_RE,
} from '@/api/targetExpression'
import { useCampaigns, useAdGroups } from '@/api/campaigns'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { isReconcileRequiredError } from '@/api/client'
import { X, AlertCircle, AlertTriangle } from 'lucide-react'
import BrowseTreePicker from '@/components/BrowseTreePicker'

type Mode = 'asin' | 'category' | 'brand'

export default function TargetDetailDrawer({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [mode, setMode] = useState<Mode>('asin')
  const [campaignId, setCampaignId] = useState<number | ''>('')
  const [adGroupId, setAdGroupId] = useState<number | ''>('')
  const [asinText, setAsinText] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [manualCategoryMode, setManualCategoryMode] = useState(false)
  const [brandId, setBrandId] = useState('')
  const [bid, setBid] = useState('0.50')
  const [error, setError] = useState<string | null>(null)
  // Scheibe S (REST-O): getrennt vom gewoehnlichen `error`, weil es eine ANDERE
  // Aussage ist. „Fehlgeschlagen" heisst „nichts passiert, gern nochmal".
  // Dieser Fall heisst „bei Amazon womoeglich passiert, lokal nicht verbucht" —
  // ein zweiter Versuch erzeugt dann ein Duplikat bei Amazon.
  const [reconcileHinweis, setReconcileHinweis] = useState<string | null>(null)
  const create = useCreateTarget()
  const campaigns = useCampaigns({ limit: 200, sortBy: 'updated_at', sortDir: 'desc' })
  const adGroups = useAdGroups(campaignId === '' ? null : Number(campaignId), { limit: 200 })

  if (!open) return null

  function buildExpression(): string | null {
    if (mode === 'asin') {
      const asins = asinText.split(/\s+/).filter(Boolean)
      const invalid = asins.filter((a) => !ASIN_RE.test(a))
      if (invalid.length > 0) {
        setError(`Ungueltige ASINs: ${invalid.slice(0, 3).join(', ')}`)
        return null
      }
      if (asins.length === 0) {
        setError('Mindestens eine ASIN erforderlich')
        return null
      }
      return buildAsinExpression(asins)
    }
    if (mode === 'category') {
      if (!categoryId.trim()) {
        setError('Kategorie-ID erforderlich')
        return null
      }
      return buildCategoryExpression(categoryId.trim())
    }
    if (!brandId.trim()) {
      setError('Brand-ID erforderlich')
      return null
    }
    return buildBrandExpression(brandId.trim())
  }

  async function handleSubmit() {
    setError(null)
    setReconcileHinweis(null)
    if (!adGroupId) {
      setError('Ad Group wählen')
      return
    }
    const expression = buildExpression()
    if (!expression) return
    try {
      await create.mutateAsync({ adGroupId: Number(adGroupId), expression, bid, state: 'enabled' })
      onClose()
    } catch (e) {
      // Scheibe S (REST-O): Der 502 der Einzel-Write-Routen (Scheibe H/O) ist kein
      // gewoehnlicher Fehlschlag — der Write kann bei Amazon gelandet sein. Er wird
      // deshalb eigens benannt, statt im roten „fehlgeschlagen"-Block unterzugehen.
      // Der Drawer bleibt offen (kein `onClose()`), damit die Aussage stehen bleibt.
      if (isReconcileRequiredError(e)) {
        setReconcileHinweis(e instanceof Error ? e.message : String(e))
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 flex h-full w-[520px] flex-col bg-[var(--background)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-semibold">Neues Product Target</h2>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium">Kampagne</label>
            <select
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value ? Number(e.target.value) : '')
                setAdGroupId('')
              }}
              className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="">— wählen —</option>
              {campaigns.data?.campaigns?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Ad Group</label>
            <select
              value={adGroupId}
              onChange={(e) => setAdGroupId(e.target.value ? Number(e.target.value) : '')}
              disabled={!campaignId}
              className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm disabled:opacity-50"
            >
              <option value="">— wählen —</option>
              {adGroups.data?.adGroups?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Erst Kampagne wählen, dann Ad Group.
            </p>
          </div>

          <div>
            <div className="mb-2 flex gap-1 rounded-md bg-[var(--accent)] p-1">
              {(['asin', 'category', 'brand'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 rounded px-3 py-1 text-sm ${
                    mode === m ? 'bg-[var(--background)] font-medium' : 'text-[var(--muted-foreground)]'
                  }`}
                >
                  {m === 'asin' ? 'ASIN-Liste' : m === 'category' ? 'Kategorie' : 'Marke'}
                </button>
              ))}
            </div>

            {mode === 'asin' && (
              <div>
                <label className="mb-1 block text-sm font-medium">ASINs (eine pro Zeile oder Leerzeichen)</label>
                <textarea
                  value={asinText}
                  onChange={(e) => setAsinText(e.target.value)}
                  rows={6}
                  placeholder={`B0073RSTXI\nB07CWVPDL1`}
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 font-mono text-sm"
                />
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  Format: B0XXXXXXXX (10 Zeichen, mit B0 beginnend)
                </p>
              </div>
            )}
            {mode === 'category' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium">
                    {manualCategoryMode ? 'Kategorie-ID' : 'Kategorie wählen'}
                  </label>
                  <button
                    type="button"
                    onClick={() => setManualCategoryMode((v) => !v)}
                    className="text-xs text-[var(--muted-foreground)] underline hover:text-[var(--foreground)]"
                  >
                    {manualCategoryMode ? 'Tree-Picker verwenden' : 'Eigene ID eingeben'}
                  </button>
                </div>
                {manualCategoryMode ? (
                  <input
                    value={categoryId}
                    onChange={(e) => {
                      setCategoryId(e.target.value)
                      setCategoryName('')
                    }}
                    placeholder="z.B. 1063498"
                    className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm font-mono"
                  />
                ) : (
                  <BrowseTreePicker
                    value={categoryId}
                    onChange={(id, name) => {
                      setCategoryId(id)
                      setCategoryName(name)
                    }}
                  />
                )}
                {categoryId && (
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Ausgewählt: <strong>{categoryName || '—'}</strong>
                    <span className="ml-1 font-mono">(ID: {categoryId})</span>
                  </p>
                )}
              </div>
            )}
            {mode === 'brand' && (
              <div>
                <label className="mb-1 block text-sm font-medium">Brand-ID</label>
                <input
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  placeholder="z.B. ID Dinkel"
                  className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                />
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  Brand-Name oder Brand-ID. Beispiele: „ID Dinkel", „Stahlia", „Neuhall".
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Bid (€)</label>
            <input
              value={bid}
              onChange={(e) => setBid(e.target.value)}
              className="h-9 w-32 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-right tabular-nums text-sm"
            />
          </div>

          {reconcileHinweis && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {`Amazon-Zustand ungeklärt — NICHT erneut anlegen, sonst entsteht dort ein Duplikat. Erst bei Amazon nachsehen. (${reconcileHinweis})`}
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-[var(--border)] px-4 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={IS_DEMO_MODE || create.isPending}
            title={IS_DEMO_MODE ? 'Demo-Modus aktiv — Mutation blockiert' : ''}
            className="rounded-md bg-[var(--primary)] px-4 py-1.5 text-sm text-[var(--primary-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {create.isPending ? 'Speichere...' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  )
}
